import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { SignaturePadHandle } from '../components/SignatureCanvas'
import { DocumentSignPanel } from '../features/legal/DocumentSignPanel'
import type { DocumentStatement } from '../features/legal/DocumentSignPanel'
import { EmailCodeForm } from '../features/account/EmailCodeForm'
import { useTranslation } from 'react-i18next'
import { Button, Card, CardBody, Input, Spinner, DateField } from '@structural-climbing/ui'
import { isMinor } from '../features/legal/minor'
import { EMPTY_GUARDIAN, GuardianFields, guardianComplete } from '../features/legal/GuardianFields'
import { isValidPhone } from '@structural-climbing/validation'

import { useGymTheme } from '../features/gym/useGymTheme'

interface GymInfo {
  id: string
  name: string
  slug: string
  description: string | null
  image_url: string | null
  primary_color: string | null
  secondary_color: string | null
  // False while the gym is being set up. The server refuses a request either
  // way; this only spares the visitor a form they cannot submit.
  accepting_members: boolean
  required_documents: { id: string; title: string; statements?: DocumentStatement[] }[]
  /** What a parent or guardian signs for someone under 18. Empty: ask at the desk. */
  minor_documents?: { id: string; title: string; statements?: DocumentStatement[] }[]
  /** Linked from the signing screen. Absent when the gym has none. */
  privacy_notice?: { id: string; version: number; path: string } | null
  /** Whether a signer must prove their email address before signing here. */
  require_verified_email?: boolean
  /**
   * The same for a minor's parent or guardian. It holds even where the gym
   * has no children's document and nothing is signed online.
   */
  minor_require_verified_email?: boolean
}

type PageState =
  | 'loading'
  | 'not_found'
  | 'coming_soon'
  | 'form'
  | 'signing'
  | 'submitting'
  | 'done'
  | 'verify_email'
type SignStep = 'view' | 'sign'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function GymHeader({ gym, sub }: { gym: GymInfo; sub: string }) {
  return (
    <div className="mb-6 text-center">
      {gym.image_url && (
        <img
          src={gym.image_url}
          alt={gym.name}
          className="mx-auto mb-3 h-16 w-16 rounded-xl object-cover"
        />
      )}
      <h1 className="text-2xl font-semibold">{gym.name}</h1>
      {gym.description && <p className="mt-1 text-sm text-foreground/60">{gym.description}</p>}
      <p className="mt-0.5 text-xs text-foreground/40">{sub}</p>
    </div>
  )
}

export function GuestJoinPage() {
  const { gymSlug } = useParams<{ gymSlug: string }>()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [gym, setGym] = useState<GymInfo | null>(null)

  // These pages sit outside GymLayout, so nothing else paints them. A gym's
  // join page is the first thing a new member ever sees of it, and it should
  // arrive wearing that gym's colours rather than Structural's defaults.
  useGymTheme(gym?.primary_color ?? null, gym?.secondary_color ?? null)
  const [pageState, setPageState] = useState<PageState>('loading')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  // Whether the Worker emailed the signed copy with this request.
  const [copyEmailed, setCopyEmailed] = useState(false)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  // Empty on purpose. It used to start at "18 years ago today", and anyone who
  // skipped the field was recorded as exactly 18, which hid every minor.
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [guardian, setGuardian] = useState(EMPTY_GUARDIAN)
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)

  const [sigIndex, setSigIndex] = useState(0)
  const [signStep, setSignStep] = useState<SignStep>('view')
  const [signatures, setSignatures] = useState<Map<string, Blob>>(new Map())
  // Per document: the statements the signer ticked. Starts empty for every
  // document, so a box is only ever ticked by the person signing.
  const [tickedByDoc, setTickedByDoc] = useState<Map<string, string[]>>(new Map())
  // The code a guest confirmed, and the address it was confirmed for. Typing a
  // different address afterwards means proving that one instead.
  const [verification, setVerification] = useState<{ email: string; id: string } | null>(null)
  const [docUrl, setDocUrl] = useState<string | null>(null)
  const [docIsPdf, setDocIsPdf] = useState(false)
  const [docLoading, setDocLoading] = useState(false)
  const [canvasEmpty, setCanvasEmpty] = useState(true)

  const sigRef = useRef<SignaturePadHandle | null>(null)
  const objectUrlRef = useRef<string | null>(null)

  // Someone under 18 does not sign. A parent or guardian does, on the gym's
  // children's document, and the address on this form is then theirs.
  const minor = !!dateOfBirth && isMinor(dateOfBirth)
  const signDocs = (minor ? gym?.minor_documents : gym?.required_documents) ?? []
  const minorName = `${firstName.trim()} ${lastName.trim()}`.trim()

  useEffect(() => {
    if (!gymSlug) {
      setPageState('not_found')
      return
    }
    fetch(`${apiUrl}/join-info/${encodeURIComponent(gymSlug)}`)
      .then(async (res) => {
        if (!res.ok) {
          setPageState('not_found')
          return
        }
        const data = (await res.json()) as GymInfo
        setGym(data)
        setPageState(data.accepting_members ? 'form' : 'coming_soon')
      })
      .catch(() => setPageState('not_found'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymSlug])

  useEffect(() => {
    if (pageState !== 'signing') return
    const currentDoc = signDocs[sigIndex]
    if (!currentDoc) return

    let cancelled = false
    setDocLoading(true)
    setDocUrl(null)
    setDocIsPdf(false)
    setSignStep('view')
    setCanvasEmpty(true)

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }

    fetch(`${apiUrl}/public-document/${encodeURIComponent(gymSlug ?? '')}/${currentDoc.id}`)
      .then(async (res) => {
        if (cancelled || !res.ok) return
        const blob = await res.blob()
        if (cancelled) return
        const url = URL.createObjectURL(blob)
        objectUrlRef.current = url
        setDocIsPdf(blob.type === 'application/pdf')
        setDocUrl(url)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setDocLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState, sigIndex])

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [])

  function validateForm(): boolean {
    if (!firstName.trim() || !lastName.trim() || !dateOfBirth) return false
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError(t('guestJoin.invalidEmail'))
      return false
    }
    setEmailError(null)
    return true
  }

  const guestEmail = email.trim().toLowerCase()

  // Where the gym asks for it, the address is proved before the first
  // document, so nobody draws a signature the Worker is about to refuse.
  function startSigning() {
    const codeRequired = minor
      ? (gym?.minor_require_verified_email ?? gym?.require_verified_email)
      : gym?.require_verified_email
    if (codeRequired && verification?.email !== guestEmail) {
      setErrorMsg(null)
      setPageState('verify_email')
      return
    }
    beginSigning()
  }

  // With nothing to sign online (a minor at a gym with no children's
  // document), confirming the address is the last step: the request goes out.
  function beginSigning(confirmedCodeId?: string) {
    if (signDocs.length === 0) {
      void handleSubmit(new Map(), confirmedCodeId)
      return
    }
    setSigIndex(0)
    setSignatures(new Map())
    setTickedByDoc(new Map())
    setPageState('signing')
  }

  function captureSignature(): Blob | null {
    if (!sigRef.current || sigRef.current.isEmpty()) return null
    const dataUrl = sigRef.current.toDataURL('image/png')
    const byteString = atob(dataUrl.split(',')[1])
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i)
    return new Blob([ab], { type: 'image/png' })
  }

  function handleAdvance() {
    const blob = captureSignature()
    if (!blob) return

    const docs = signDocs
    const currentDoc = docs[sigIndex]
    const newSigs = new Map(signatures)
    newSigs.set(currentDoc.id, blob)
    setSignatures(newSigs)
    sigRef.current?.clear()

    const next = sigIndex + 1
    if (next < docs.length) {
      setSigIndex(next)
    } else {
      void handleSubmit(newSigs)
    }
  }

  async function handleSubmit(sigs?: Map<string, Blob>, confirmedCodeId?: string) {
    if (!gym) return
    setPageState('submitting')
    setErrorMsg(null)

    try {
      const formData = new FormData()
      formData.append('gym_slug', gym.slug)
      formData.append('user_first_name', firstName.trim())
      formData.append('user_last_name', lastName.trim())
      formData.append('user_date_of_birth', dateOfBirth)
      formData.append('user_email', email.trim().toLowerCase())
      if (!minor && phone.trim()) formData.append('user_phone', phone.trim())
      for (const [docId, blob] of sigs ?? signatures) {
        formData.append(`sig_${docId}`, blob, `${docId}.png`)
      }
      // Sent for every document, even with nothing ticked: it is how the
      // Worker knows this build showed the statements at all.
      for (const doc of signDocs) {
        formData.append(`ticked_${doc.id}`, JSON.stringify(tickedByDoc.get(doc.id) ?? []))
      }
      if (minor) {
        formData.append('guardian_first_name', guardian.firstName.trim())
        formData.append('guardian_last_name', guardian.lastName.trim())
        formData.append('guardian_declaration', String(guardian.declared))
        formData.append('guardian_phone', guardian.phone.trim())
      }

      // Passed in when the code was confirmed a moment ago and the state has
      // not caught up with it yet.
      const codeId =
        confirmedCodeId ?? (verification?.email === guestEmail ? verification.id : null)
      if (codeId) formData.append('verification_id', codeId)
      // The language the signed copy is emailed in: a guest has no profile.
      formData.append('language', i18n.language.slice(0, 2))

      // No Authorization header — Worker detects absence and sets is_guest=true
      const res = await fetch(`${apiUrl}/join-request`, { method: 'POST', body: formData })
      const data = (await res.json()) as {
        error?: string
        code?: string
        documents_emailed?: boolean
      }

      if (!res.ok) {
        // The confirmed code is only good for a while, and reading and signing
        // can outlast it. Proving the address again is all that is needed.
        if (data.code === 'EMAIL_NOT_VERIFIED') {
          setVerification(null)
          setPageState('verify_email')
          return
        }
        setErrorMsg(data.error ?? t('common.errorDefault'))
        // Sent to the desk, or told the form is out of date: back to the form,
        // where the message can be read, not to a signing screen.
        const backToForm =
          data.code === 'GUARDIAN_REQUIRED' || data.code === 'GUARDIAN_PHONE_REQUIRED'
        setPageState(signDocs.length > 0 && !backToForm ? 'signing' : 'form')
        return
      }

      setCopyEmailed(data.documents_emailed === true)
      setPageState('done')
    } catch {
      setErrorMsg(t('common.networkError'))
      setPageState(signDocs.length > 0 ? 'signing' : 'form')
    }
  }

  if (pageState === 'loading' || pageState === 'submitting') {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  if (pageState === 'not_found') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
        <p className="text-lg font-semibold">{t('join.gymNotFound')}</p>
        <p className="text-sm text-foreground/60">{t('join.checkLink')}</p>
      </div>
    )
  }

  if (pageState === 'coming_soon') {
    return (
      <div className="mx-auto w-full max-w-md p-6">
        <GymHeader gym={gym!} sub={t('guestJoin.dayPassSubtitle')} />
        <Card>
          <CardBody className="flex flex-col items-center gap-2 py-8 text-center">
            <p className="text-base font-semibold">{t('join.comingSoonTitle')}</p>
            <p className="text-sm text-foreground/60">{t('join.comingSoonBody')}</p>
          </CardBody>
        </Card>
      </div>
    )
  }

  if (pageState === 'done') {
    return (
      <div className="mx-auto w-full max-w-md p-6">
        <GymHeader gym={gym!} sub={t('guestJoin.dayPassSubtitle')} />
        <Card>
          <CardBody className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="text-4xl">✓</div>
            <p className="text-base font-semibold">{t('guestJoin.doneTitle')}</p>
            <p className="text-sm text-foreground/60">{t('guestJoin.doneSubtitle')}</p>
            {copyEmailed && <p className="text-sm text-foreground">{t('legal.copyEmailed')}</p>}
          </CardBody>
        </Card>
      </div>
    )
  }

  if (pageState === 'verify_email' && gym) {
    return (
      <div className="mx-auto w-full max-w-md p-6">
        <GymHeader gym={gym} sub={t('guestJoin.dayPassSubtitle')} />
        <Card>
          <CardBody className="flex flex-col gap-4 p-6">
            <h2 className="text-lg font-semibold">{t('emailCode.beforeSigningTitle')}</h2>
            <p className="text-sm text-foreground">{t('emailCode.beforeSigningBody')}</p>
            <EmailCodeForm
              email={guestEmail}
              guest={{ gymSlug: gym.slug, email: guestEmail }}
              onVerified={(result) => {
                setVerification({ email: guestEmail, id: result.verificationId })
                // Already signed everything and only the confirmation had
                // lapsed: send what was drawn, do not make them sign again.
                if (signatures.size > 0 && signatures.size === signDocs.length)
                  void handleSubmit(signatures, result.verificationId)
                else beginSigning(result.verificationId)
              }}
            />
            <Button variant="light" size="sm" onClick={() => setPageState('form')}>
              {t('emailCode.changeEmail')}
            </Button>
          </CardBody>
        </Card>
      </div>
    )
  }

  const docs = signDocs
  const currentDoc = docs[sigIndex]

  if (pageState === 'signing' && currentDoc) {
    const total = docs.length
    const isLastDoc = sigIndex === total - 1

    return (
      <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col gap-4 p-4 sm:p-6">
        <div className="flex shrink-0 items-center gap-3">
          {gym?.image_url && (
            <img src={gym.image_url} alt={gym.name} className="h-10 w-10 rounded-lg object-cover" />
          )}
          <div>
            <p className="text-sm font-semibold">{gym?.name}</p>
            <p className="text-xs text-foreground/40">
              {t('join.documentOf', { current: sigIndex + 1, total })}
            </p>
          </div>
        </div>

        {/* min-h-0 flex-1 all the way down, so the document gives up height
            before the sign button does. RootLayout is h-dvh and this chain
            reaches it, which means the card fits whatever the nav bars leave. */}
        <div className="flex min-h-0 flex-1 flex-col gap-3 rounded-2xl border border-divider bg-content1 p-4 shadow-sm sm:p-6">
          <h2 className="shrink-0 text-base font-semibold">{currentDoc.title}</h2>
          {minor && (
            <p className="shrink-0 text-sm font-medium text-primary">
              {t('legal.signingAsGuardian', { name: minorName })}
            </p>
          )}

          <DocumentSignPanel
            step={signStep}
            docTitle={currentDoc.title}
            docUrl={docUrl}
            docIsPdf={docIsPdf}
            docLoading={docLoading}
            error={errorMsg}
            signLabel={isLastDoc ? t('join.signAndSubmit') : t('join.signAndContinue')}
            canvasEmpty={canvasEmpty}
            statements={currentDoc.statements}
            ticked={tickedByDoc.get(currentDoc.id) ?? []}
            onTickedChange={(ids) => setTickedByDoc(new Map(tickedByDoc).set(currentDoc.id, ids))}
            privacyNoticeUrl={gym?.privacy_notice ? `${apiUrl}${gym.privacy_notice.path}` : null}
            sigRef={sigRef}
            onCanvasEnd={() => setCanvasEmpty(sigRef.current?.isEmpty() ?? true)}
            onClear={() => {
              sigRef.current?.clear()
              setCanvasEmpty(true)
            }}
            onProceed={() => setSignStep('sign')}
            onBack={() => setSignStep('view')}
            onSign={handleAdvance}
          />
        </div>
      </div>
    )
  }

  // Form state
  const hasRequiredDocs = docs.length > 0
  // A gym with no children's document still takes a minor: the parent is
  // named and proves an address, nothing is signed online, and the gym app
  // tells the desk to have the agreement signed on paper.
  const minorOnPaper = minor && docs.length === 0
  const canProceed =
    firstName.trim() &&
    lastName.trim() &&
    dateOfBirth &&
    EMAIL_RE.test(email.trim()) &&
    (!minor || guardianComplete(guardian, { needsEmail: false, needsPhone: true }))
  const phoneError =
    !minor && phone.trim() && !isValidPhone(phone) ? t('join.phoneInvalidError') : ''

  return (
    <div className="mx-auto w-full max-w-md p-6">
      <GymHeader gym={gym!} sub={t('guestJoin.dayPassSubtitle')} />

      <Card>
        <CardBody className="flex flex-col gap-4 py-5">
          <div>
            <p className="text-base font-semibold">{t('guestJoin.guestTitle')}</p>
            <p className="mt-0.5 text-xs text-foreground/50">
              {hasRequiredDocs ? t('guestJoin.withDocs') : t('guestJoin.withoutDocs')}
            </p>
          </div>

          <Input
            label={t('join.firstNameLabel')}
            placeholder={t('join.firstNamePlaceholder')}
            value={firstName}
            onValueChange={setFirstName}
            isRequired
          />
          <Input
            label={t('join.lastNameLabel')}
            placeholder={t('join.lastNamePlaceholder')}
            value={lastName}
            onValueChange={setLastName}
            isRequired
          />
          <DateField
            label={t('join.dateOfBirthLabel')}
            value={dateOfBirth}
            onValueChange={setDateOfBirth}
            isRequired
            max={new Date().toISOString().slice(0, 10)}
          />
          {minor && (
            <GuardianFields
              value={guardian}
              onChange={setGuardian}
              minorName={minorName}
              gymName={gym!.name}
              withPhone
            />
          )}
          {minorOnPaper && (
            <p role="note" className="text-sm text-foreground">
              {t('legal.minorPaperNotice')}
            </p>
          )}
          <Input
            label={minor ? t('legal.guardianEmail') : t('join.emailLabel')}
            type="email"
            placeholder="alex@example.com"
            value={email}
            onValueChange={(v) => {
              setEmail(v)
              setEmailError(null)
            }}
            isRequired
            isInvalid={!!emailError}
            errorMessage={emailError ?? undefined}
          />
          {/* A minor guest gives the parent's phone, in the block above. */}
          {!minor && (
            <Input
              label={t('join.phoneLabel')}
              type="tel"
              placeholder={t('join.phonePlaceholder')}
              value={phone}
              onValueChange={setPhone}
              isInvalid={!!phoneError}
              errorMessage={phoneError || undefined}
            />
          )}

          {hasRequiredDocs && (
            <div className="rounded-lg border border-divider bg-content2/50 p-3">
              <p className="mb-1 text-xs font-medium text-foreground/70">{t('join.mustSign')}</p>
              <ul className="flex flex-col gap-0.5">
                {docs.map((doc) => (
                  <li key={doc.id} className="text-xs text-foreground/60">
                    · {doc.title}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {errorMsg && <p className="text-sm text-danger">{errorMsg}</p>}

          <Button
            color="primary"
            isDisabled={!canProceed || !!phoneError}
            onClick={() => {
              if (!validateForm()) return
              if (hasRequiredDocs || minor) {
                startSigning()
              } else {
                void handleSubmit()
              }
            }}
            className="w-full"
          >
            {hasRequiredDocs ? t('guestJoin.nextSignDocs') : t('guestJoin.submitButton')}
          </Button>
        </CardBody>
      </Card>
    </div>
  )
}
